import React, { createContext, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { initiateGoogleLogin } from '../utils/auth.js';
import useAuthStateMachine, { AUTH_STATES } from '../hooks/useAuthStateMachine.js';

const AuthContext = createContext();

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const location = useLocation();
  const isPublicRoute = ['/login', '/auth/callback'].includes(location.pathname);
  
  const {
    state,
    user,
    role,
    permissions,
    error,
    retryCount,
    isLoading,
    isAuthenticated,
    hasError,
    isExpired,
    login: stateMachineLogin,
    logout: stateMachineLogout,
    handleCallback,
    retry,
    resetError,
    canRetry,
    initialize
  } = useAuthStateMachine(isPublicRoute);

  // Google OAuth login handler
  const login = useCallback(async (authData) => {
    return await stateMachineLogin(authData);
  }, [stateMachineLogin]);

  // Logout handler
  const logout = useCallback(() => {
    stateMachineLogout();
  }, [stateMachineLogout]);

  // Initiate Google OAuth flow
  const loginWithGoogle = useCallback(() => {
    initiateGoogleLogin();
  }, []);

  // OAuth callback handler
  const handleOAuthCallback = useCallback(async (urlFragment, state) => {
    return await handleCallback(urlFragment, state);
  }, [handleCallback]);

  // Check if user has specific permission
  const hasPermission = useCallback((permission) => {
    return permissions && permissions.includes(permission);
  }, [permissions]);

  // Check if user has specific role
  const hasRole = useCallback((requiredRole) => {
    if (requiredRole === 'admin') {
      return role === 'admin';
    }
    return role === requiredRole || role === 'admin';
  }, [role]);

  // Get current session info
  const getSessionInfo = useCallback(() => {
    if (!isAuthenticated) return null;
    return { user, role, permissions, isAuthenticated };
  }, [user, role, permissions, isAuthenticated]);

  const contextValue = {
    // State machine state
    state,
    error,
    retryCount,
    hasError,
    isExpired,
    canRetry,
    
    // User state
    user,
    role,
    permissions,
    isAuthenticated,
    isLoading,
    
    // Authentication methods
    login,
    logout,
    loginWithGoogle,
    handleOAuthCallback,
    initialize,
    
    // Error handling
    retry,
    resetError,
    
    // Permission checks
    hasPermission,
    hasRole,
    
    // Session utilities
    getSessionInfo,
    
    // Legacy compatibility (for existing components)
    username: user?.name || user?.email || '',
    loading: isLoading, // Legacy loading prop
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AUTH_STATES };
export default AuthContext;